import { useEffect, useState } from "react";
import { Loader2, MapPin, Plus, Trash2 } from "lucide-react";
import bizApi from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { btnPrimary, cardCls, inputCls } from "./ui";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const CATEGORIES = ["hotel", "airport", "station", "restaurant", "company", "clinic", "fbo", "residence", "venue", "other"];

export default function Places() {
  const { t } = useBiz();
  const [places, setPlaces] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", category: "hotel" });

  const load = () => bizApi.get("/places").then((r) => setPlaces(r.data)).catch(() => setPlaces([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    await bizApi.post("/places", form).catch(() => {});
    setForm({ name: "", address: "", category: "hotel" });
    load();
  };

  const remove = async (id) => {
    await bizApi.delete(`/places/${id}`).catch(() => {});
    load();
  };

  return (
    <div data-testid="places-view" className="max-w-3xl">
      <h1 className="text-2xl font-bold tracking-tight text-ink">{t("places.title")}</h1>
      <p className="mt-0.5 text-sm text-slate">{t("places.subtitle")}</p>

      <div className={`${cardCls} mt-5 p-5`}>
        <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_160px_auto]">
          <input className={inputCls} placeholder={t("places.name")} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="place-name" />
          <AddressAutocomplete value={form.address} testId="place-address" inputClass={inputCls}
            placeholder={t("places.address")} onChange={(v) => setForm({ ...form, address: v })} />
          <select className={inputCls} value={form.category} data-testid="place-category"
            onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{t(`places.cat.${c}`)}</option>)}
          </select>
          <button type="button" onClick={add} disabled={!form.name || !form.address}
            className={btnPrimary} data-testid="place-add">
            <Plus className="h-4 w-4" /> {t("places.new")}
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {!places && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div>}
        {places && places.length === 0 && (
          <div className={`${cardCls} p-10 text-center`} data-testid="places-empty">
            <p className="text-sm text-slate">{t("places.empty")}</p>
          </div>
        )}
        {(places || []).map((p) => (
          <div key={p.id} className={`${cardCls} flex items-center gap-3 p-4`} data-testid={`place-${p.id}`}>
            <MapPin className="h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">{p.name}</p>
              <p className="truncate text-xs text-slate">{p.address}</p>
            </div>
            <span className="rounded-full bg-ink/[0.05] px-2.5 py-1 text-[10px] font-medium text-slate">{t(`places.cat.${p.category}`)}</span>
            <button type="button" onClick={() => remove(p.id)} className="text-slate hover:text-red-500" data-testid={`place-delete-${p.id}`}>
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
